/**
 * ExecutiveSummariesPage — LLM-written executive reports ("board packs").
 * Stitches recent investigations, triggered alerts, governance stats and
 * workspace activity into an online collapsible view + downloadable PDF,
 * generated on demand or on a weekly/monthly schedule with email/Slack/Teams
 * delivery. Scoped to the current tenant.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Presentation, Loader2, Download, Trash2, CalendarClock, Sparkles, Copy,
  ChevronDown, ChevronRight, Check, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { SlackChannelPicker, TeamsChannelPicker } from '@/components/ChannelPickers';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CLS = {
  running: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30',
  completed: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  failed: 'bg-destructive/15 text-destructive border-destructive/30',
};

const inputCls = 'w-full px-3 py-2 rounded-lg bg-muted/40 border border-border/50 text-sm text-foreground outline-none focus:border-primary/50';

function fmtDate(v) {
  if (!v) return '';
  try { return new Date(v).toLocaleString(); } catch { return String(v).slice(0, 16); }
}

export default function ExecutiveSummariesPage() {
  const [packs, setPacks] = useState([]);
  const [periodDays, setPeriodDays] = useState(7);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [schedule, setSchedule] = useState({ cadence: 'off' });
  const [emailsText, setEmailsText] = useState('');
  const [slackChannels, setSlackChannels] = useState([]);
  const [teamsChannels, setTeamsChannels] = useState([]);
  const [slackConnected, setSlackConnected] = useState(false);
  const [teamsConnected, setTeamsConnected] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [online, setOnline] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const [loadingOnlineId, setLoadingOnlineId] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [pRes, sRes] = await Promise.all([
        api.getExecSummaries(),
        api.getExecSummarySchedule(),
      ]);
      const items = pRes.items || [];
      setPacks(items);
      setSchedule(sRes || { cadence: 'off' });
      setEmailsText((sRes?.notify_emails || []).join(', '));
      setSlackChannels(sRes?.notify_slack_channels || []);
      setTeamsChannels(sRes?.notify_teams_channels || []);
      // Only surface a delivery channel that's actually connected for this tenant.
      api.listSlackChannels().then(() => setSlackConnected(true)).catch(() => setSlackConnected(false));
      api.listTeamsChannels().then(() => setTeamsConnected(true)).catch(() => setTeamsConnected(false));
      return items;
    } catch (e) {
      toast.error(e.message || 'Failed to load executive summaries');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [load]);

  // Poll while any pack is still running.
  useEffect(() => {
    const anyRunning = packs.some((p) => p.status === 'running');
    if (anyRunning && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const items = await load();
        if (!items.some((p) => p.status === 'running')) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setGenerating(false);
        }
      }, 3000);
    }
    if (!anyRunning && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [packs, load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.generateExecSummary(periodDays);
      if (res.status === 'running' && res.id) {
        toast.info('An executive summary is already being generated');
      } else {
        toast.success('Generating executive summary — this can take up to a minute');
      }
      await load();
    } catch (e) {
      toast.error(e.message || 'Failed to start generation');
      setGenerating(false);
    }
  };

  const saveSchedule = async () => {
    setSavingSchedule(true);
    try {
      const cfg = {
        cadence: schedule.cadence,
        notify_emails: emailsText.split(/[,\n]/).map((s) => s.trim()).filter(Boolean),
        notify_slack_channels: slackChannels,
        notify_teams_channels: teamsChannels,
      };
      const res = await api.updateExecSummarySchedule(cfg);
      setSchedule(res);
      toast.success('Schedule saved');
    } catch (e) {
      toast.error(e.message || 'Failed to save schedule');
    } finally {
      setSavingSchedule(false);
    }
  };

  const toggleOnline = async (p) => {
    if (online[p.id]) {
      setOnline((prev) => { const n = { ...prev }; delete n[p.id]; return n; });
      return;
    }
    setLoadingOnlineId(p.id);
    try {
      const doc = await api.getExecSummary(p.id);
      setOnline((prev) => ({ ...prev, [p.id]: doc }));
    } catch (e) {
      toast.error(e.message || 'Failed to open online view');
    } finally {
      setLoadingOnlineId(null);
    }
  };

  const copySection = async (packId, section) => {
    try {
      await navigator.clipboard.writeText(section.text || '');
      const k = `${packId}:${section.key}`;
      setCopiedKey(k);
      toast.success(`Copied "${section.title}"`);
      setTimeout(() => setCopiedKey((cur) => (cur === k ? null : cur)), 1600);
    } catch {
      toast.error('Copy failed — browser blocked clipboard access');
    }
  };

  const copyShareText = async (p) => {
    try {
      const res = await api.getExecSummaryShareText(p.id);
      await navigator.clipboard.writeText(res.text || '');
      toast.success('Share text copied to clipboard');
    } catch (e) {
      toast.error(e.message || 'Share text unavailable');
    }
  };

  const download = (p) => {
    window.open(`${API_BASE}/exec-summaries/${p.id}/download`, '_blank');
  };

  const remove = async (p) => {
    if (!window.confirm('Delete this executive summary?')) return;
    try {
      await api.deleteExecSummary(p.id);
      toast.success('Deleted');
      setOnline((prev) => { const n = { ...prev }; delete n[p.id]; return n; });
      load();
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="exec-summaries-page">
      <div className="flex items-start gap-3">
        <div className="p-2.5 rounded-xl bg-primary/10 text-primary"><Presentation className="w-6 h-6" /></div>
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Executive Briefings</h1>
          <p className="text-sm text-muted-foreground mt-0.5 max-w-2xl">
            An AI-written executive report that stitches your recent investigations, triggered alerts,
            governance coverage and workspace activity into a shareable summary and downloadable PDF.
          </p>
        </div>
      </div>

      {/* Generate */}
      <div className="rounded-2xl border border-border/50 bg-card/40 p-5 space-y-3" data-testid="exec-generate-card">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Sparkles className="w-4 h-4 text-primary" /> Generate a summary
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-xs text-muted-foreground">Period</label>
          <select
            value={periodDays}
            onChange={(e) => setPeriodDays(Number(e.target.value))}
            className={`${inputCls} max-w-[160px]`}
            data-testid="exec-period-select"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button onClick={generate} disabled={generating} data-testid="exec-generate-btn">
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
            Generate now
          </Button>
          <Button variant="outline" onClick={load} data-testid="exec-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Schedule */}
      <div className="rounded-2xl border border-border/50 bg-card/40 p-5 space-y-3" data-testid="exec-schedule-card">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <CalendarClock className="w-4 h-4 text-primary" /> Automatic schedule & delivery
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-xs text-muted-foreground">Cadence</label>
            <select
              value={schedule.cadence || 'off'}
              onChange={(e) => setSchedule((s) => ({ ...s, cadence: e.target.value }))}
              className={inputCls}
              data-testid="exec-cadence-select"
            >
              <option value="off">Off</option>
              <option value="weekly">Weekly (Mondays)</option>
              <option value="monthly">Monthly (1st)</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Notify emails (comma or newline separated)</label>
            <Input value={emailsText} onChange={(e) => setEmailsText(e.target.value)}
              placeholder="cfo@acme.com, board@acme.com" className={inputCls} data-testid="exec-emails" />
          </div>
          {slackConnected && (
          <div>
            <label className="text-xs text-muted-foreground">Slack channels</label>
            <div className="mt-1"><SlackChannelPicker value={slackChannels} onChange={setSlackChannels} /></div>
          </div>
          )}
          {teamsConnected && (
          <div>
            <label className="text-xs text-muted-foreground">Teams channels</label>
            <div className="mt-1"><TeamsChannelPicker value={teamsChannels} onChange={setTeamsChannels} /></div>
          </div>
          )}
        </div>
        <Button onClick={saveSchedule} disabled={savingSchedule} data-testid="exec-save-schedule-btn">
          {savingSchedule ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
          Save schedule
        </Button>
      </div>

      {/* List */}
      <div className="space-y-3" data-testid="exec-list">
        {loading ? (
          <div className="text-center py-10 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : packs.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border/50 p-10 text-center">
            <Presentation className="w-8 h-8 mx-auto text-muted-foreground/70 mb-2" />
            <p className="text-sm text-muted-foreground">No executive summaries yet. Generate your first one above.</p>
          </div>
        ) : packs.map((p) => (
          <div key={p.id} className="rounded-2xl border border-border/50 bg-card/40 overflow-hidden" data-testid={`exec-row-${p.id}`}>
            <div className="p-4 flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">{p.title || 'Executive Summary'}</span>
                  <Badge className={`text-[10px] border ${STATUS_CLS[p.status] || ''}`}>{p.status}</Badge>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {fmtDate(p.created_at)} · last {p.period_days} days
                  {p.status === 'completed' && (
                    <> · {p.investigations_count || 0} investigation(s) · {p.alerts_count || 0} alert(s) · {p.coverage_pct || 0}% governed</>
                  )}
                  {p.status === 'running' && p.progress_stage && <> · {p.progress_stage}…</>}
                  {p.status === 'failed' && p.error && <span className="text-destructive"> · {p.error}</span>}
                </div>
              </div>
              {p.status === 'completed' && (
                <div className="flex items-center gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => toggleOnline(p)} data-testid={`exec-view-${p.id}`}>
                    {loadingOnlineId === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : online[p.id] ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    <span className="ml-1">View</span>
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => download(p)} data-testid={`exec-download-${p.id}`}>
                    <Download className="w-3.5 h-3.5" /><span className="ml-1">PDF</span>
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => copyShareText(p)} data-testid={`exec-share-${p.id}`}>
                    <Copy className="w-3.5 h-3.5" /><span className="ml-1">Share</span>
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(p)} data-testid={`exec-delete-${p.id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-destructive" />
                  </Button>
                </div>
              )}
              {p.status !== 'completed' && (
                <Button size="sm" variant="ghost" onClick={() => remove(p)} data-testid={`exec-delete-${p.id}`}>
                  <Trash2 className="w-3.5 h-3.5 text-destructive" />
                </Button>
              )}
            </div>

            {online[p.id] && (
              <div className="border-t border-border/40 divide-y divide-border/30" data-testid={`exec-online-${p.id}`}>
                {(online[p.id].sections || []).map((sec) => {
                  const k = `${p.id}:${sec.key}`;
                  const isCollapsed = collapsed[k];
                  return (
                    <div key={sec.key} className="px-4 py-3">
                      <div className="flex items-center justify-between">
                        <button
                          className="flex items-center gap-1.5 text-sm font-medium text-foreground"
                          onClick={() => setCollapsed((prev) => ({ ...prev, [k]: !prev[k] }))}
                          data-testid={`exec-section-toggle-${sec.key}`}
                        >
                          {isCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                          {sec.title}
                        </button>
                        <Button size="sm" variant="ghost" onClick={() => copySection(p.id, sec)}>
                          {copiedKey === k ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                        </Button>
                      </div>
                      {!isCollapsed && (
                        <pre className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap font-sans leading-relaxed">{sec.text}</pre>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
